import { FC } from "react";
import { useChatContext } from "../../ChatProvider";
import { setLoadingState, setError, deleteMessage } from "../../store/chatSlice";
import { LoadingState } from "../../types";
import { useAgentChat } from "../../hooks/useAgentChat";
import { IconRefresh } from "@tabler/icons-react";
import { Button, Tooltip } from "@altimateai/lego";

interface RegenerateButtonProps {
  messageId: string;
}

export const RegenerateButton: FC<RegenerateButtonProps> = ({ messageId }) => {
  const { sendMessage } = useAgentChat();
  const { dispatch, currentSessionId, sessions } = useChatContext();

  const handleRegenerate = async () => {
    if (!currentSessionId) return;

    const currentSession = sessions[currentSessionId];
    if (!currentSession) return;

    try {
      dispatch(
        setLoadingState({
          state: LoadingState.LOADING,
          sessionId: currentSessionId,
        })
      );

      // Get the last user message before this assistant message
      const messages = currentSession.messages;
      const currentIndex = messages.findIndex(msg => msg.id === messageId);
      if (currentIndex === -1) return;

      const lastUserMessage = messages
        .slice(0, currentIndex)
        .filter(msg => msg.role === "user")
        .pop();

      if (!lastUserMessage) {
        throw new Error("No user message found to regenerate");
      }

      // Delete the current assistant message
      dispatch(
        deleteMessage({
          sessionId: currentSessionId,
          messageId,
        })
      );

      // Call API with the last user message
      await sendMessage({
        message: lastUserMessage.content,
        dontAddUserMessage: true,
      });
    } catch (err) {
      dispatch(
        setError({
          message: err instanceof Error ? err.message : "Failed to regenerate response",
        })
      );
    } finally {
      dispatch(
        setLoadingState({
          state: LoadingState.UNINITIALIZED,
          sessionId: currentSessionId,
        })
      );
    }
  };

  return (
    <Tooltip title={"Regenerate response"}>
      <Button
        variant="ghost"
        size="sm"
        className={"al-text-gray-500"}
        onClick={handleRegenerate}
        aria-label="Regenerate response"
      >
        <IconRefresh aria-hidden="true" />
      </Button>
    </Tooltip>
  );
};
